import { observable, computed, action } from "mobx";
import MediaItemDownloadInfo from "./MediaItemDownloadInfo";
import MediaItemClassName from "~/constants/Media/MediaItemClassName";

class MediaItem {
  /**
   * @property {number|null}
   */
  @observable photoId = null;

  /**
   * @property {number|null}
   */
  @observable virtualTourId = null;

  /**
   * @property {number|null}
   */
  @observable videoClipId = null;

  /**
   * @property {number}
   */
  @observable brandingMode;

  /**
   * @property {string}
   */
  @observable description;

  /**
   * @property {number}
   */
  @observable displayDuration;

  /**
   * @property {string}
   */
  @observable duration;

  /**
   * @property {number}
   */
  @observable height;

  /**
   * @property {string}
   */
  @observable includeIn;

  /**
   * @property {string}
   */
  @observable kenBurns;

  /**
   * @property {string}
   */
  @observable name;

  /**
   * @property {number}
   */
  @observable sequence;

  /**
   * @property {string}
   */
  @observable imagePathWebLarge;

  /**
   * @property {string}
   */
  @observable imagePathWebSmall;

  /**
   * @property {string}
   */
  @observable thumbnail;

  /**
   * @property {number}
   */
  @observable tourId;

  /**
   * @property {string} className the actual media class name. One of Photo, VirtualTour, VideoClip.
   */
  @observable className;

  /**
   * @property {number}
   */
  @observable width;

  /**
   * @property {bool}
   */
  @observable isNavigationLayer = false;

  // The following props are only concerned with the download behavior.

  @observable thumbnailLarge;

  @observable downloads = [];

  @observable isSelected;

  @observable isEditing;

  @observable isSlot;

  @observable panRight;

  constructor({
    photoId = null,
    virtualTourId = null,
    videoClipId = null,
    brandingMode = 0,
    description = "",
    displayDuration = 0,
    duration = "",
    height = 0,
    includeIn = "",
    kenBurns = "",
    name = "",
    sequence = 0,
    imagePathWebLarge = "",
    imagePathWebSmall = "",
    thumbnail = "",
    tourId = 0,
    // Default type when no className is provided. Fixes issue when an empty mediaItem is created.
    className = MediaItemClassName.PHOTO,
    uuid = 0,
    width = 0,
    isNavigationLayer = false,
    thumbnailLarge = "",
    downloads = [],
    isSelected = false,
    isEditing = false,
    isSlot = false,
    panRight = false,
  }) {
    this.photoId = photoId;
    this.virtualTourId = virtualTourId;
    this.videoClipId = videoClipId;
    this.brandingMode = brandingMode;
    this.description = description;
    this.displayDuration = displayDuration;
    this.duration = duration;
    this.height = height;
    this.includeIn = includeIn;
    this.kenBurns = kenBurns;
    this.name = name;
    this.sequence = sequence;
    this.imagePathWebLarge = imagePathWebLarge;
    this.imagePathWebSmall = imagePathWebSmall;
    this.thumbnail = thumbnail;
    this.tourId = tourId;
    this.className = className;
    this.uuid = uuid;
    this.width = width;
    this.isNavigationLayer = isNavigationLayer;
    this.thumbnailLarge = thumbnailLarge;
    this.isSelected = isSelected;
    this.isEditing = isEditing;
    this.isSlot = isSlot;
    this.panRight = panRight;

    if (className && Object.values(MediaItemClassName).indexOf(className) === -1) {
      throw new Error(`Unsupported media className: ${className}`);
    }

    downloads.forEach(download => {
      // Pass the media "id" and "className" to the MediaDownloadInfo so we can generate a fully qualified input name
      // that can be submitted for bulk downloading.
      this.downloads.push(new MediaItemDownloadInfo({ ...download, mediaItemId: this.id, className }));
    });
  }

  isVideoClip() {
    return this.className === MediaItemClassName.VIDEO_CLIP;
  }

  isVirtualTour() {
    return this.className === MediaItemClassName.VIRTUAL_TOUR;
  }

  isPhoto() {
    return this.className === MediaItemClassName.PHOTO;
  }

  @action
  setIsSelected(isSelected) {
    this.isSelected = isSelected;
  }

  @action
  setIsEditing(isEditing) {
    this.isEditing = isEditing;
  }

  @action
  setName(name) {
    this.name = name;
  }

  @computed
  get isVisibilityNeither() {
    return this.includeIn === "neither";
  }

  @computed
  get isVisibilityBoth() {
    return this.includeIn === "both";
  }

  @computed
  get isVisibilityGallery() {
    return this.includeIn === "gallery";
  }

  @computed
  get id() {
    if (this.isPhoto()) {
      return this.photoId;
    }

    if (this.isVirtualTour()) {
      return this.virtualTourId;
    }

    if (this.isVideoClip()) {
      return this.videoClipId;
    }

    throw new Error(
      `The class name of the Media Item cannot be something other than: Photo, VirtualTour, VideoClip. Got: ${this.className}`,
    );
  }

  @computed
  get mediaItemId() {
    return `${this.className}_${this.id}`;
  }

  @action
  setIncludeIn(includeIn) {
    this.includeIn = includeIn;
  }

  /**
   * Gather the necessary data for posting to the API. Note that not all fields are writable; also that
   * depending on the media type some fields won't be included.
   * @param {Array} excludeProps if given, we'll remove these props from the formData. Useful when the API
   *        expects an exact number of properties.
   */
  getFormData(excludeProps = []) {
    this.normalizeDuration();

    const formData = {
      id: this.id,
      className: this.className,
      name: this.name,
      description: this.description,
      brandingMode: this.brandingMode,
      includeIn: this.includeIn,
    };

    if (this.isPhoto()) {
      formData.isNavigationLayer = this.isNavigationLayer;
      formData.duration = this.duration;
    }

    if (this.isVirtualTour()) {
      formData.duration = this.duration;
      formData.panRight = this.panRight;
    }

    // duration is not included in VideoClip since it's read only.

    excludeProps.forEach(prop => {
      delete formData[prop];
    });

    return formData;
  }

  normalizeDuration() {
    // normalize duration since the legacy backend validation is quite picky.
    if (this.duration === "" || this.duration == null || this.duration === undefined) {
      this.duration = null;
    } else {
      // cast duration to int since there's a non empty value.
      this.duration = parseInt(this.duration, 10);

      if (Number.isNaN(this.duration)) {
        throw new Error("Unexpected mediaItem duration value");
      }
    }
  }
}

export default MediaItem;
